import './App.css';
import {useEffect, useRef, useState} from "react";

import {FiSend} from "react-icons/fi";
import {FaHourglassHalf} from "react-icons/fa";

import {motion} from "framer-motion";

function ThinkingIndicator() {
    return (
        <motion.span
            className="h-3 w-3 rounded-full thinking-indicator"
            animate={{
                scale: [1, 1.4, 1],      // 살짝 커졌다 돌아오고
                opacity: [1, 0.4, 1],    // 동시에 투명도 변화
            }}
            transition={{
                duration: 1.2,           // 한 사이클 1.2초
                repeat: Infinity,        // 무한 반복
                ease: "easeInOut",
            }}
        />
    );
}

function render() {
    const src = document.getElementById("video");
    const mask = document.getElementById("mask");
    const cvs = document.getElementById("canvas");
    const ctx = cvs.getContext("2d");

    const maskCvs = new OffscreenCanvas(1, 1);            // 오프스크린으로 처리
    const mCtx = maskCvs.getContext('2d', {willReadFrequently: true});

    if (src.readyState >= 2 && mask.readyState >= 2) {
        const w = cvs.width = src.videoWidth;
        const h = cvs.height = src.videoHeight;

        // 1) 원본 프레임
        ctx.drawImage(src, 0, 0, w, h);

        // 2) ===== 마스크: 그레이 → 알파 변환 =====
        maskCvs.width = w;
        maskCvs.height = h;
        mCtx.drawImage(mask, 0, 0, w, h);

        const frame = mCtx.getImageData(0, 0, w, h);
        const d = frame.data;              // Uint8ClampedArray

        // R(또는 (R+G+B)/3) → A 복사
        for (let i = 0; i < d.length; i += 4) {
            const lum = d[i];                // d[i] = R; 그레이라면 R=G=B
            d[i + 3] = lum;                 // alpha 채널
            d[i] = d[i + 1] = d[i + 2] = 255;    // 흰색(색상은 중요 X)
        }
        mCtx.putImageData(frame, 0, 0);

        // 3) 합성
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(maskCvs, 0, 0, w, h);
        ctx.globalCompositeOperation = 'source-over';

        // console.log("Rendered frame with mask applied");
    }
    requestAnimationFrame(render);
}

async function fetchMessage(logs) {
    const response = await fetch("/api/chat", {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({logs})
    });

    if (!response.ok) {
        throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return data.message;
}

function App() {
    const [logs, setLogs] = useState([]);
    const [isActive, setIsActive] = useState(true);
    const [inputText, setInputText] = useState("");

    const [gesture, setGesture] = useState("idle");

    const videoRefIdle = useRef(null);
    const videoRefSmile = useRef(null);
    const videoRefTilt = useRef(null);

    const videoMap = {
        idle: videoRefIdle,
        smile: videoRefSmile,
        tilt: videoRefTilt,
    }

    const personName = "Bot";

    useEffect(() => {
        // scroll to the bottom of the chat logs whenever logs change
        const logsContainer = document.querySelector(".logs");
        if (logsContainer) {
            logsContainer.scrollTop = logsContainer.scrollHeight;
        }
    }, [logs]);

    async function submitMessage() {
        if (inputText.trim() === "") return;
        if (!isActive) return;

        setIsActive(false);
        setInputText("");
        setLogs((prevLogs) => [...prevLogs, inputText]);

        try {
            const response = await fetchMessage([...logs, inputText]);

            console.log("Response from server:", response);

            if (response.includes("*웃음*")) {
                console.log("Detected smile gesture");
                setGesture("smile");
            }

            if (response.includes("*갸우뚱*")) {
                console.log("Detected tilt gesture");
                setGesture("tilt");
            }

            // clear the gesture after processing
            const clearedResponse = response.replace(/\*웃음\*|\*갸우뚱\*|\*IDLE\*/g, "").trim();

            setLogs((prevLogs) => [...prevLogs, clearedResponse]);
        } catch (error) {
            console.error("Error fetching message:", error);
            setLogs((prevLogs) => [...prevLogs, `Error fetching response: ${error.message}`]);
        }

        setIsActive(true);
    }

    useEffect(() => {
        const idleInterval = setInterval(() => {
            if (gesture !== "idle") {
                return; // idle 상태가 아니면 아무 동작도 하지 않음
            }
            videoRefIdle.current.play().catch((error) => {
                console.error("Error playing idle video:", error);
            });
        }, 5000); // 5초에서 10초 사이의 랜덤 시간

        return () => clearInterval(idleInterval);
    }, []);

    function onVideoEnded(videoGesture) {
        if (videoGesture === "idle") {
            return; // idle 상태에서는 비디오가 끝나도 아무 동작을 하지 않음
        }

        if (gesture !== videoGesture) {
            return; // 현재 제스처와 비디오 제스처가 다르면 아무 동작을 하지 않음
        }

        setGesture("idle"); // 비디오가 끝나면 idle 상태로 돌아감
    }

    useEffect(() => {
        videoMap[gesture]?.current?.play().catch((error) => {
            console.error("Error playing video:", error);
        });
    }, [gesture]);

    return (
        <div className="wrapper">
            <video id={"video"} muted playsInline loop/>
            <video id={"mask"} muted playsInline loop/>
            <div className="screen">
                <img id="body-image" src="/data/body_image.png" alt="Body"/>
                <video id="canvas" ref={videoRefIdle} playsInline
                       style={{display: `${gesture === "idle" ? "" : "none"}`}} muted>
                    <source src={`/data/cut_idle.mov`} type='video/quicktime; codecs="hvc1"'/>
                    <source src={`/data/cut_idle.webm`} type='video/webm; codecs=vp09.00.51.08'/>
                </video>
                <video id="canvas" ref={videoRefSmile} playsInline
                       style={{display: `${gesture === "smile" ? "" : "none"}`}} muted
                       onEnded={() => onVideoEnded("smile")}>
                    <source src={`/data/cut_smile.mov`} type='video/quicktime; codecs="hvc1"'/>
                    <source src={`/data/cut_smile.webm`} type='video/webm; codecs=vp09.00.51.08'/>
                </video>
                <video id="canvas" ref={videoRefTilt} playsInline
                       style={{display: `${gesture === "tilt" ? "" : "none"}`}} muted
                       onEnded={() => onVideoEnded("tilt")}>
                    <source src={`/data/cut_tilt.mov`} type='video/quicktime; codecs="hvc1"'/>
                    <source src={`/data/cut_tilt.webm`} type='video/webm; codecs=vp09.00.51.08'/>
                </video>
                <div className={"chat"}>
                    <div className={"logs"}>
                        {
                            logs.map((log, index) => {
                                if (index % 2 === 0) {
                                    return <div key={index} className={"msg log-user"}>{log}</div>;
                                } else {
                                    return (
                                        <div key={index} className={"log-bot-container"}>
                                            <span className={"bot-name"}>{personName}</span>
                                            <div className={"msg log-bot"}>
                                                {log}
                                            </div>
                                        </div>
                                    );
                                }
                            })
                        }
                        {
                            !isActive && (
                                <div className={"log-bot-container"}>
                                    <span className={"bot-name"}>{personName} <ThinkingIndicator/></span>
                                    <div className={"msg log-bot log-bot-thinking"}>
                                        Thinking...
                                    </div>
                                </div>
                            )
                        }
                    </div>
                    <div className={"input-area"}>
                        <input id={"input-text"} type="text" placeholder="Type your message here..." value={inputText}
                               onChange={(e) => setInputText(e.target.value)}
                               onKeyDown={(e) => {
                                   if (e.key === 'Enter' && isActive) {
                                       submitMessage();
                                   }
                               }}
                        />
                        <button id={"input-button"} onClick={submitMessage}>
                            {isActive ? <FiSend size={18}/> : <FaHourglassHalf size={18}/>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default App;
